import { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import { swaggerFetch } from '@src/hooks/useFetch';
import { listMarioAgentThreadMessages } from '@src/services/community';
import { IMessageItem, ImRole } from '@src/components/common/chat-list/type';
import { ImWsClient, WsEnvelope, ActionType, EventType } from './im-socket';
import useImmer from '@/hooks/useImmer';
const LOADING_HEIGHT = 40;

/** 8 位十六进制随机串（4 字节 CSPRNG，碰撞概率可忽略） */
function randomHash8(): string {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export const generateThreadId = () => {
  return `mario-webchat-${randomHash8()}`;
};

const SOCKET_URL = `wss://${window.location.hostname}/marioclaw/api/ws/agent-chat`;
const DEFAULT_IM_BOX_INFO_REF = {
  hasMore: false,
  isLoading: false,
  isError: false,
  cursor: '',
  streamContent: '',
  pageSize: 1000,
};

export enum SystemMessageType {
  // 解散群
  DISMISS_GROUP = 'dismiss_group',
  // 移除成员
  REMOVE_MEMBER = 'remove_member',
  NONE = 'none',
}
const FLAG_MAP = {
  currentChannelId: '',
};
export const setCurrentChannelId = (channelId: string) => {
  FLAG_MAP.currentChannelId = channelId;
};
export const getCurrentChannelId = () => {
  return FLAG_MAP.currentChannelId;
};
export enum StreamStatus {
  READY = 'ready',
  STREAMING = 'streaming',
  DONE = 'done',
  ERROR = 'error',
  NONE = 'none',
}
export interface CreateThreadParams {
  content: string;
  threadId: string;
  marioAgentId: string;
  timestamp: string;
}
export const useMessageModel = () => {
  const [state, updateState] = useImmer({
    list: [] as IMessageItem[],
    hasMore: true,
    isLoading: false,
    isError: false,
    cursor: '',
    streamStatus: StreamStatus.NONE,
    streamMessage: {
      content: '',
      role: ImRole.ASSISTANT,
      timestamp: String(new Date().getTime()),
    },
  });
  const [isNewCreatePending, setIsNewCreatePending] = useState(true);
  const imBoxInfoRef = useRef({ ...DEFAULT_IM_BOX_INFO_REF });
  const imSocketRef = useRef<ImWsClient | null>(null);
  const scrollEleRef = useRef<HTMLDivElement | null>(null);
  const isFirstFetchRef = useRef(true);
  const lastScrollHeightRef = useRef(0);
  const threadIdRef = useRef('');
  const agentIdRef = useRef('');

  const setThreadId = useCallback((isCreate: boolean, threadId: string) => {
    setIsNewCreatePending(isCreate);
    threadIdRef.current = threadId;
  }, []);
  const setAgentId = useCallback((agentId: string) => {
    agentIdRef.current = agentId;
  }, []);

  const scrollToBottom = useCallback(() => {
    const timer = requestAnimationFrame(() => {
      if (scrollEleRef.current) {
        scrollEleRef.current.scrollTop = scrollEleRef.current.scrollHeight ?? 0;
        cancelAnimationFrame(timer);
      }
    });
  }, []);

  const onMessage = useCallback(
    (message: WsEnvelope) => {
      const { action, data } = message;
      if (action === ActionType.Broadcast && data?.eventType) {
        // 同一 WebSocket 连接会收到所有会话的下行；按 thread / agent 过滤，避免切会话后串台
        const incomingThreadId = data.threadId;
        if (
          incomingThreadId &&
          incomingThreadId !== threadIdRef.current
        ) {
          return;
        }
        const incomingAgentId = data.marioAgentId;
        if (
          incomingAgentId &&
          incomingAgentId !== agentIdRef.current
        ) {
          return;
        }
        switch (data?.eventType) {
          case EventType.START:
            imBoxInfoRef.current.streamContent = data?.content ?? '';
            updateState((draft) => {
              draft.streamStatus = StreamStatus.STREAMING;
              draft.streamMessage.content = imBoxInfoRef.current.streamContent;
              draft.streamMessage.timestamp = String(new Date().getTime());
            });
            break;
          case EventType.TOKEN:
            imBoxInfoRef.current.streamContent += data?.content ?? '';
            updateState((draft) => {
              draft.streamMessage.content = imBoxInfoRef.current.streamContent;
            });
            break;
          case EventType.TASK_COMPLETED:
            const streamContent = imBoxInfoRef.current.streamContent;
            imBoxInfoRef.current.streamContent = '';
            updateState((draft) => {
              draft.streamMessage.content = '';
              draft.streamStatus = StreamStatus.NONE;
              draft.list.push({
                content: streamContent,
                role: ImRole.ASSISTANT,
                isShowThinking: true,
                timestamp: draft.streamMessage.timestamp,
              });
            });
            break;
          case EventType.ERROR:
            updateState((draft) => {
              draft.streamMessage.content = '';
              draft.streamStatus = StreamStatus.NONE;
              draft.list.push({
                content: `[${data.errorCode}]: ${data.errorMsg}`,
                role: ImRole.ASSISTANT,
                isShowThinking: true,
                timestamp: draft.streamMessage.timestamp,
              });
            });
            break;
          default:
            break;
        }
        scrollToBottom();
      }
    },
    [scrollToBottom],
  );

  useEffect(() => {
    imSocketRef.current = new ImWsClient({
      url: SOCKET_URL,
    });
    imSocketRef.current.onMessage(onMessage);
    imSocketRef.current.connect();
    return () => {
      imSocketRef.current?.disconnect();
    };
  }, [onMessage]);

  // // 发送消息
  const onSendMessage = useCallback(
    (content: string, createCallback: (params: CreateThreadParams) => void) => {
      console.log('onSendMessage', content);
      const params = {
        content,
        threadId: threadIdRef.current,
        marioAgentId: agentIdRef.current,
        timestamp: String(new Date().getTime()),
      };
      if (isNewCreatePending) {
        createCallback(params);
        setIsNewCreatePending(false);
      }
      imSocketRef.current?.sendMessage(params);
      updateState((draft) => {
        draft.streamStatus = StreamStatus.READY;
        draft.list.push({
          content,
          role: ImRole.SELF,
        });
      });
      scrollToBottom();
    },
    [scrollToBottom, isNewCreatePending],
  );

  // // 加载更多消息
  const loadMore = useCallback(async (threadId: string) => {
    // 第一次请求时，重置状态
    if (isFirstFetchRef.current) {
      imBoxInfoRef.current = { ...DEFAULT_IM_BOX_INFO_REF };
      updateState((draft) => {
        draft.list = [];
        draft.hasMore = true;
        draft.cursor = '';
        draft.isLoading = false;
        draft.isError = false;
        draft.streamStatus = StreamStatus.NONE;
        draft.streamMessage.content = '';
        draft.streamMessage.timestamp = String(new Date().getTime());
      });
    }
    // 设置加载中状态
    imBoxInfoRef.current.isLoading = true;
    updateState((draft) => {
      draft.isLoading = true;
    });
    const { isOk, data } = await swaggerFetch(
      listMarioAgentThreadMessages,
      {
        threadId,
        marioAgentId: agentIdRef.current,
      },
      {
        headers: {
          disabledToast: true,
        },
      },
    );
    if (isOk && data) {
      imBoxInfoRef.current.hasMore = false;
      imBoxInfoRef.current.isLoading = false;
      imBoxInfoRef.current.isError = false;
      updateState((draft) => {
        const list = (data ?? []).reduce((acc: IMessageItem[], item: any) => {
          acc.push(
            {
              id: `${item.taskId}-user`,
              content: item.userMessage,
              role: ImRole.SELF,
              timestamp: item.createdAt,
            },
            {
              id: `${item.taskId}-assistant`,
              content: item.assistantMessage,
              role: ImRole.ASSISTANT,
              timestamp: item.completedAt,
            },
          );
          return acc;
        }, [] as IMessageItem[]);
        draft.list.push(...list);
        draft.hasMore = false;
        draft.isLoading = false;
        draft.isError = false;
      });
      // 滚动到指定位置
      const timer = window.requestAnimationFrame(() => {
        if (scrollEleRef.current) {
          scrollEleRef.current.scrollTop = isFirstFetchRef.current
            ? scrollEleRef.current.scrollHeight
            : scrollEleRef.current.scrollHeight -
              lastScrollHeightRef.current -
              LOADING_HEIGHT;
        }
        cancelAnimationFrame(timer);
      });
    } else {
      imBoxInfoRef.current.isLoading = false;
      imBoxInfoRef.current.isError = true;
      updateState((draft) => {
        draft.isLoading = false;
        draft.isError = true;
      });
    }
    isFirstFetchRef.current = false;
  }, []);

  // // 重试
  const onRetry = useCallback(
    async () => loadMore(threadIdRef.current),
    [loadMore],
  );
  // // 刷新消息列表
  const onRefresh = useCallback(
    async (threadId: string) => {
      isFirstFetchRef.current = true;
      loadMore(threadId).then(() => {
        scrollToBottom();
      });
    },
    [loadMore, scrollToBottom],
  );
  const updateScrollElRef = useCallback((el: HTMLDivElement | null) => {
    scrollEleRef.current = el as HTMLDivElement;
  }, []);

  const onScrollHandler = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { hasMore, isLoading, isError } = imBoxInfoRef.current;
      if (isLoading || isError || !hasMore) return;
      const { scrollTop, scrollHeight } = e.target as HTMLDivElement;
      if (scrollTop <= 0) {
        lastScrollHeightRef.current = scrollHeight;
        loadMore(threadIdRef.current);
      }
    },
    [loadMore],
  );

  return {
    state,
    setAgentId,
    isNewCreatePending,
    setThreadId,
    onSendMessage,
    onRetry,
    onRefresh,
    updateScrollElRef,
    onScrollHandler,
  };
};
